import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import DialogContentText from '@material-ui/core/DialogContentText';
import { PropTypes } from 'prop-types';
import { fetchDistributionCenters, selectDistributionCenter } from 'actions/DistributionCenters';
import { connect } from 'react-redux';
import { sendNotification, errorNotification } from 'actions/Notifications';
import { fetchDcOrders, confirmDcOrderArrival, fetchDcReceivedOrders } from 'actions/TransferCheck';
import Title from '../components/Title';
import SubTitle from '../components/SubTitle';
import DcBox from '../components/DcBox';
import LoadingDcs from '../components/LoadingDcs';
import LoadingOrders from '../components/LoadingOrders';
import FooterBackButton from '../components/FooterBackButton';
import OrderCard from '../components/OrderCard';
import { Typography } from '@material-ui/core';
import {
	BUCKETS,
	BUCKET_LABELS,
	INVENTORY_BUCKETS,
	hasOrdersInConference,
	orderBucket,
} from 'utils/inventoryBuckets';

const WARNING_STYLE = { color: '#f44336', marginBottom: '16px', fontWeight: 'bold' };

export const OrderArrivalContainer = props => {
	const [step, setStep] = useState(0);
	const [dc, setDc] = useState();
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState({});

	const isValidationEnabled = process.env.ORDER_ARRIVAL_VALIDATION === 'ON';
	const conferenceOf = bucket =>
		props.receivedOrdersByBucket && props.receivedOrdersByBucket[bucket];
	const ecommerceConference = conferenceOf(INVENTORY_BUCKETS.ECOMMERCE);
	const hasLegacyConference =
		isValidationEnabled &&
		hasOrdersInConference(ecommerceConference) &&
		!ecommerceConference.bucket;
	const isBucketLocked = bucket =>
		hasLegacyConference || (isValidationEnabled && hasOrdersInConference(conferenceOf(bucket)));
	const showsBuckets = (props.orders || []).some(
		order => orderBucket(order) === INVENTORY_BUCKETS.VAREJO
	);

	useEffect(() => {
		props.fetchDistributionCenters();
		selectDistributionCenter && props.fetchDcOrders();
	}, []);

	useEffect(() => {
		if (
			props.distributionCenters &&
			props.distributionCenters.data &&
			props.distributionCenters.data.length > 0 &&
			localStorage.getItem('distributionCenter')
		) {
			const distributionCenter = props.distributionCenters.data.find(
				d => d._id === localStorage.getItem('distributionCenter')
			);

			setStep(1);
			setDc(distributionCenter);
		}
	}, [props.distributionCenters, props.orders]);

	useEffect(() => {
		if (isValidationEnabled && step === 1) {
			props.fetchDcReceivedOrders();
		}
	}, [step, isValidationEnabled]);

	const handleDcClick = name => {
		const distributionCenter = props.distributionCenters.data.find(d => d.name === name);
		setStep(1);
		props.selectDistributionCenter(
			distributionCenter._id,
			distributionCenter.address,
			distributionCenter.kind
		);
		setDc(distributionCenter);
		props.fetchDcOrders();
	};

	const renderFooterButton = () => <FooterBackButton onBack={handleBack} />;

	const handleBack = () => {
		setStep(0);
		setDc();
	};

	const handleOrderClick = (number, arrival) => {
		setConfirmModalOpen(true);
		setSelectedOrder({ number, arrival });
	};

	const handleConfirmModalClose = () => {
		setConfirmModalOpen(false);
		setSelectedOrder({});
	}

	const handleConfirmOrder = async () => {
		await props.confirmDcOrderArrival(selectedOrder.number);

		if (isValidationEnabled) {
			await props.fetchDcReceivedOrders();
		}

		setConfirmModalOpen(false);
	}

	const renderConfirmModal = () => (
		<Dialog open={confirmModalOpen} onClose={handleConfirmModalClose}>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					Confirma a chegada da Ordem <b>{selectedOrder.number}</b>,
					<br />
					prevista para <b>{selectedOrder.arrival}</b>
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleConfirmOrder} color="primary">
					Sim
				</Button>
				<Button onClick={handleConfirmModalClose} color="primary" autoFocus>
					Não
				</Button>
			</DialogActions>
		</Dialog>
	);

	const renderDcs = () => (
		<Grid>
			{props.distributionCenters.loading ? (
				<LoadingDcs />
			) : (
				props.distributionCenters.data
					.filter(dc => dc.kind !== 'Warehouse')
					.map(dc => <DcBox name={dc.name} onClick={handleDcClick} />)
			)}
		</Grid>
	);

	const renderOrders = () => {
		if (props.ordersLoading) {
			return (<LoadingOrders />);
		}
		return mapOrders(props.orders);
	}

	const renderBucketOrders = (orders, bucket) => {
		const bucketOrders = orders.filter(order => orderBucket(order) === bucket);
		if (!bucketOrders.length) {
			return null;
		}
		const locked = isBucketLocked(bucket);
		const label = BUCKET_LABELS[bucket].toLowerCase();
		return (
			<React.Fragment key={bucket}>
				{showsBuckets && (
					<Typography variant="h6" style={{ marginTop: '8px', marginBottom: '8px' }}>
						{BUCKET_LABELS[bucket]}
					</Typography>
				)}
				{showsBuckets && locked && !hasLegacyConference && (
					<Typography style={WARNING_STYLE}>
						{`Já existe uma ordem de ${label} em conferência. Finalize a conferência dela antes de confirmar novas chegadas de ${label}.`}
					</Typography>
				)}
				{bucketOrders.map(order => (
					<OrderCard
						key={order.number}
						number={order.number}
						arrival={order.arrival}
						onClick={handleOrderClick}
						disabled={locked}
					/>
				))}
			</React.Fragment>
		);
	};

	const mapOrders = orders => (
		<React.Fragment>
			{orders.length ? (
				BUCKETS.map(bucket => renderBucketOrders(orders, bucket))
			) : (
				<Typography>
					{`Não existem ordens agendadas para hoje neste CD (${dc.name})`}
				</Typography>
			)}
		</ React.Fragment>
	);

	const subTitleMessage = `${dc && dc.name}`;

	const renderWarningMessage = () => {
		const locksWholeDc =
			hasLegacyConference || (!showsBuckets && isBucketLocked(INVENTORY_BUCKETS.ECOMMERCE));
		if (locksWholeDc && step === 1) {
			return (
				<Typography style={WARNING_STYLE}>
					Já existe uma ordem em conferência. Finalize a conferência antes de confirmar novas chegadas.
				</Typography>
			);
		}
		return null;
	};

	return (
		<Grid container direction="column">
			<Title />
			{renderWarningMessage()}
			<SubTitle>{(dc && subTitleMessage) || 'Escolha o local onde deseja começar'}</SubTitle>
			{renderConfirmModal()}
			{step === 0 && renderDcs()}
			{step === 1 && renderOrders()}
			{step === 1 && renderFooterButton()}
		</Grid>
	);
};

const mapStateToProps = state => ({
	distributionCenters: state.distributionCenters,
	orders: state.transferCheck.orders,
	ordersLoading: state.transferCheck.ordersLoading,
	receivedOrdersByBucket: state.transferCheck.receivedOrdersByBucket,
});

OrderArrivalContainer.propTypes = {
	sendNotification: PropTypes.func.isRequired,
	errorNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, {
	fetchDcOrders,
	confirmDcOrderArrival,
	fetchDcReceivedOrders,
	selectDistributionCenter,
	fetchDistributionCenters,
	sendNotification,
	errorNotification,
})(OrderArrivalContainer);
